import styled, { css, keyframes } from 'styled-components';

/**
 * Slide-in from above the canvas, matching the floating top toolbar's
 * entry animation so the two surfaces feel like siblings of the same
 * floating-overlay layer.
 */
const enterFromTop = keyframes`
  from { opacity: 0; transform: translate(-50%, -6px); }
  to   { opacity: 1; transform: translate(-50%, 0); }
`;

/** Generous enough for the longest "Not runnable end-to-end" copy; clamps below the viewport on narrow canvases. */
const MAX_WIDTH = '460px';
/** Slightly below the floating top toolbar's `top: 8px` so the two pills stack rather than overlap. */
const TOP_OFFSET = '56px';
/**
 * Caps the issue list to exactly three rows before scrolling kicks in —
 * anything beyond scrolls within the panel.
 *
 * <p>The realistic row in this list is a 1-line title (~16px) + 2px gap
 * + 1-line hint (~16px) inside 12px of vertical padding ≈ 46px. Sizing
 * for the worst-case 2-line title (~62px) over-shoots and lets a 4th
 * row peek in for the typical case, which is what AIB-6988's reporter
 * was seeing. The +12px slack covers the {@link IssueList} top +
 * bottom padding (4px + 8px); long messages that wrap to two lines may
 * push the third row to scroll, which is the conscious trade-off.</p>
 */
const LIST_MAX_HEIGHT = 'calc((46px * 3) + 12px)';

/**
 * Outer absolute-positioned wrapper that pins the floating legend over
 * the canvas. Pointer events are scoped so panning + box-select on the
 * canvas under the pill still work — only the pill itself catches the
 * cursor.
 */
export const LegendRoot = styled.div<{ $expanded: boolean }>`
  position: absolute;
  top: ${TOP_OFFSET};
  left: 50%;
  transform: translateX(-50%);
  z-index: 12;
  pointer-events: none;
  display: inline-flex;
  flex-direction: column;
  width: max-content;
  max-width: min(${MAX_WIDTH}, calc(100% - 16px));
  background: ${({ theme: { colors = {} } }) => colors.surface ?? '#0E0F18'};
  /* Pill collapses to a full-radius capsule; rounds out to 24px when the
     issue list is expanded so the bottom edge reads as a single rounded
     panel rather than a pill stuck above a flat list. */
  border-radius: ${({ $expanded }) => ($expanded ? '24px' : '999px')};
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.4);
  user-select: none;
  overflow: hidden;
  animation: ${enterFromTop} 220ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
  transition: border-radius 160ms ease;
  & > * {
    pointer-events: auto;
  }
`;

export const LegendRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 8px;
  min-height: 32px;
`;

export const LegendHead = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 6px 6px 8px;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  text-align: left;
  border-radius: 999px;
  min-width: 0;
  flex: 1;
`;

/**
 * Disc that frames the title icon. Three tone variants — success /
 * caution / error — colour-mix to a transparent tint of the theme's
 * status colour so the disc reads as a tinted halo rather than a flat
 * fill.
 *
 * <p>The {@code error} variant fires when a recent run failed on one or
 * more nodes; the legend escalates from amber to red so the runtime
 * failure outranks "this graph has a couple of lint warnings".</p>
 */
type LegendTone = 'success' | 'caution' | 'error';

function toneColor(
  tone: LegendTone,
  colors: { success?: string; caution?: string; error?: string },
): string {
  if (tone === 'success') return colors.success ?? '#57A15B';
  if (tone === 'error') return colors.error ?? '#E54B4B';
  return colors.caution ?? '#D8A300';
}

export const StatusIcon = styled.span<{ $tone: LegendTone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  flex-shrink: 0;
  & > .material-symbols-rounded {
    font-size: 22px;
    font-variation-settings: 'FILL' 1, 'wght' 500;
  }

  ${({ $tone, theme: { colors = {} } }) => {
    const fg = toneColor($tone, colors);
    return css`
      color: ${fg};
      background: color-mix(in oklch, ${fg} 16%, transparent);
    `;
  }}
`;

export const Copy = styled.span`
  display: inline-flex;
  flex-direction: column;
  line-height: 1.2;
  min-width: 0;
`;

export const Title = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Sub = styled.span`
  font-size: 11.5px;
  color: ${({ theme: { colors = {} } }) => colors.onSurfaceVariant ?? '#A1A3B8'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CountBadge = styled.span<{ $tone?: LegendTone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 4px;
  flex-shrink: 0;
  ${({ $tone = 'caution', theme: { colors = {} } }) => {
    const fg = toneColor($tone, colors);
    return css`
      color: ${fg};
      background: color-mix(in oklch, ${fg} 22%, transparent);
    `;
  }}
`;

export const IssueList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 4px 6px 8px;
  max-height: ${LIST_MAX_HEIGHT};
  overflow-y: auto;
`;

/**
 * Divider label that separates the blocking rows from the ones that only
 * matter at publish time, so the author can tell "fix before you run"
 * from "fix before you share" at a glance.
 */
export const SectionLabel = styled.li`
  padding: 8px 6px 4px 14px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme: { colors = {} } }) => colors.onSurfaceVariant ?? '#A1A3B8'};
`;

export const IssueItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 6px 6px 14px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 120ms ease;

  &:hover {
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainerHigh ?? '#1F2030'};
  }
  &:active {
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainerHighest ?? '#262738'};
  }
  &:focus-visible {
    outline: none;
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainerHigh ?? '#1F2030'};
    box-shadow:
      0 0 0 2px
        ${({ theme: { colors = {} } }) => colors.surface ?? '#0E0F18'},
      0 0 0 4px
        ${({ theme: { colors = {} } }) =>
          colors.primary
            ? `color-mix(in oklch, ${colors.primary} 60%, transparent)`
            : 'rgba(193, 193, 255, 0.6)'};
  }
  &:hover .legend-issue__arrow {
    color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  }
`;

export const Bullet = styled.span<{ $tone?: LegendTone }>`
  width: 6px;
  height: 6px;
  border-radius: 999px;
  flex-shrink: 0;
  background: ${({ $tone = 'caution', theme: { colors = {} } }) =>
    toneColor($tone, colors)};
`;

export const ItemCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

export const ItemTitle = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  line-height: 1.3;
  overflow-wrap: anywhere;
`;

export const ItemHint = styled.span`
  font-size: 11.5px;
  color: ${({ theme: { colors = {} } }) => colors.onSurfaceVariant ?? '#A1A3B8'};
  line-height: 1.35;
`;

export const ItemArrow = styled.span.attrs({ className: 'legend-issue__arrow' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  color: ${({ theme: { colors = {} } }) => colors.onSurfaceVariant ?? '#A1A3B8'};
  flex-shrink: 0;
  margin-left: auto;
  transition: color 120ms ease;
  & > .material-symbols-rounded {
    font-size: 20px;
  }
`;
